import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarClock, Inbox } from "lucide-react";
import { Badge } from "@/dentlab/src/components/ui/badge";
import { cn } from "@/dentlab/src/lib/utils/cn";
import { usePatientStore } from "@/dentlab/src/store/Patient";
import { JobStatusEnum } from "@/lib/types/job";

export interface UpcomingJob {
	id: number;
	code: string | null;
	title: string | null;
	status: number;
	outgoing_order_date: string;
	client_id: string;
	client_name: string;
	patient_first_name: string | null;
	patient_last_name: string | null;
}

interface UpcomingJobsWidgetProps {
	jobs: UpcomingJob[];
	onJobClick: (job: UpcomingJob) => void;
	agendaLink?: string;
	className?: string;
}

const STATUS_BORDER_COLORS: Record<number, string> = {
	[JobStatusEnum.REQUESTED]: "border-l-gray-400",
	[JobStatusEnum.IN_PROGRESS]: "border-l-blue-500",
	[JobStatusEnum.COMPLETED]: "border-l-emerald-500",
	[JobStatusEnum.ARCHIVED]: "border-l-red-500",
	[JobStatusEnum.BOOKED_SINGLE]: "border-l-amber-500",
	[JobStatusEnum.BOOKED_MONTHLY]: "border-l-amber-500",
	[JobStatusEnum.PAID_SINGLE]: "border-l-slate-400",
	[JobStatusEnum.PAID_MONTHLY]: "border-l-slate-400",
};

interface JobGroup {
	label: string;
	jobs: UpcomingJob[];
}

const groupJobs = (jobs: UpcomingJob[]): JobGroup[] => {
	const now = new Date();
	const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
	const tomorrowStart = new Date(todayStart);
	tomorrowStart.setDate(tomorrowStart.getDate() + 1);

	const overdue: UpcomingJob[] = [];
	const today: UpcomingJob[] = [];
	const next7d: UpcomingJob[] = [];

	for (const job of jobs) {
		const d = new Date(job.outgoing_order_date);
		if (d < todayStart) {
			overdue.push(job);
		} else if (d < tomorrowStart) {
			today.push(job);
		} else {
			next7d.push(job);
		}
	}

	return [
		{ label: "Überfällig", jobs: overdue },
		{ label: "Heute", jobs: today },
		{ label: "Nächste 7 Tage", jobs: next7d },
	].filter((g) => g.jobs.length > 0);
};

export const UpcomingJobsWidget: React.FC<UpcomingJobsWidgetProps> = ({
	jobs,
	onJobClick,
	agendaLink,
	className,
}) => {
	const patientNameFn = usePatientStore((state) => state.patientName);
	const groups = groupJobs(jobs);

	return (
		<div
			className={cn(
				"bg-white rounded-lg border border-gray-200 overflow-hidden flex flex-col",
				className
			)}
			data-testid="upcoming-jobs-widget"
		>
			<div className="flex items-center gap-2 px-3 py-2.5 bg-gray-50 flex-shrink-0">
				<CalendarClock className="h-4 w-4 text-gray-500" />
				<h3 className="text-sm font-semibold text-gray-900">Aufträge</h3>
				{jobs.length > 0 && (
					<Badge
						variant="secondary"
						className="h-5 min-w-[20px] px-1.5 text-xs"
					>
						{jobs.length}
					</Badge>
				)}
				{agendaLink && (
					<Link
						to={agendaLink}
						className="ml-auto flex items-center gap-1 text-xs text-gray-500 hover:text-gray-900 transition-colors"
						aria-label="Zur Agenda"
						data-testid="agenda-page-link"
					>
						<span>Agenda</span>
						<ArrowRight className="h-3 w-3" />
					</Link>
				)}
			</div>

			<div className="border-t border-gray-200" />

			{jobs.length === 0 ? (
				<div className="flex-1 flex items-center justify-center pb-3">
					<div className="text-center">
						<Inbox className="h-6 w-6 mx-auto mb-1 text-gray-300" />
						<p className="text-xs text-gray-400">
							Keine anstehenden Aufträge
						</p>
					</div>
				</div>
			) : (
				<div className="flex-1 min-h-0 overflow-y-auto">
					{groups.map((group, gi) => (
						<React.Fragment key={group.label}>
							<div className={cn("flex items-center gap-2 px-3 py-1", gi > 0 && "mt-1")}>
								<div className="flex-1 border-t border-gray-200" />
								<span className="text-xs text-gray-400 whitespace-nowrap">
									{group.label}
								</span>
								<div className="flex-1 border-t border-gray-200" />
							</div>
							{group.jobs.map((job) => {
								const patientName =
									job.patient_first_name ||
									job.patient_last_name
										? patientNameFn({
												patient_id: "",
												first_name:
													job.patient_first_name ??
													"",
												last_name:
													job.patient_last_name ??
													"",
												code: "",
												title: "",
											})
										: "";

								return (
									<div
										key={job.id}
										className={cn(
											"border-l-[3px] px-3 py-1 hover:bg-gray-50 cursor-pointer transition-colors",
											STATUS_BORDER_COLORS[job.status] ??
												"border-l-gray-300"
										)}
										onClick={() => onJobClick(job)}
										data-testid={`upcoming-job-${job.id}`}
										aria-label={`Auftrag öffnen: ${job.code || ""} ${job.title || ""}`}
									>
										<div className="flex items-center gap-2 min-w-0">
											<span className="text-xs font-medium text-gray-500 flex-shrink-0">
												{job.code}
											</span>
											<span className="text-sm text-gray-900 truncate">
												{job.title}
											</span>
										</div>
										<div className="flex items-center gap-2 min-w-0">
											<span className="text-xs text-gray-500 truncate">
												{job.client_name}
											</span>
											{patientName && (
												<>
													<span className="text-xs text-gray-300">
														·
													</span>
													<span className="text-xs text-gray-400 truncate">
														{patientName}
													</span>
												</>
											)}
										</div>
									</div>
								);
							})}
						</React.Fragment>
					))}
				</div>
			)}
		</div>
	);
};
